import { faSortDown, faSortUp, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { SortTypeEnum, styles } from "@repo/helpers";

import { Button } from "../basic/button";
import { Spinner } from "../basic/spinner";
import { Column } from "./models";
import Pagination from "./pagination";
import style from "./style.module.scss";
import ToggleColumnsDropdown from "./toggleColumnsDropdown";
import { GridMarkupProps } from "./types";

/**
 * Get the grid markup
 * @param props
 */
export default function GridMarkup<T>(props: GridMarkupProps<T>) {
	const {
		id,
		tableClassName,
		containerClassName,
		tableContainerClassName,
		currentPage,
		totalItems,
		pageSize,
		getData,
		title,
		loading,
		setItemsPerPage,
		hidePagination,
		pageChanged,
	} = props;
	const thead = renderHead();
	const tbody = renderBody();
	return (
		<div className={styles(style.gridContainer, containerClassName)}>
			{title && (
				<div className={style.title}>
					<h4>{title}</h4>
					<ActionsContainer {...props} />
				</div>
			)}
			<div className={styles(style.tableContainer, tableContainerClassName)} id={id}>
				<div className={style.tableOverlay}>&nbsp;</div>
				<table className={styles(style.table, tableClassName)} ref={props.tableRef}>
					{thead}
					{tbody}
				</table>
			</div>
			{!hidePagination && (
				<Pagination
					currentPage={currentPage}
					totalItems={totalItems}
					pageChanged={pageChanged}
					setItemsPerPage={setItemsPerPage}
					itemsPerPage={pageSize}
					refresh={props.refresh}
					canSetItemsPerPage={props.canSetItemsPerPage}
					pageSizes={props.pageSizes}
				/>
			)}
			<div
				className={style.loadingOverlay}
				style={{
					display: loading ? "block" : "none",
				}}
			>
				<div>
					<span>
						<FontAwesomeIcon icon={faSpinner} pulse key="loading" />
					</span>
				</div>
			</div>
		</div>
	);

	/**
	 * Get the table head for the grid
	 */
	function renderHead() {
		const { columns, headerClassName, columnClassName } = props;
		if (columns === undefined) return null;
		return (
			<thead>
				<tr className={styles(style.tableHeader, headerClassName)}>{columns.map(renderColumn)}</tr>
			</thead>
		);

		function renderColumn(column: Column<T>, index: number) {
			const cellClass = styles(style.tableColumn, columnClassName, props.resizeable && style.resizeableHeader);
			return (
				<th
					className={cellClass}
					key={index}
					style={{
						minWidth: column.minWidth || null,
						display: column.visible ? null : "none",
						width: column.width || null,
					}}
					onClick={() => props.sortEventHandler(index)}
				>
					{column.name}
					{sortIcon()}
					{resizer()}
				</th>
			);
			function sortIcon() {
				if (column.sortBy === SortTypeEnum.none) return null;
				return (
					<FontAwesomeIcon
						icon={column.sortBy === SortTypeEnum.ascending ? faSortDown : faSortUp}
						key="sort"
					/>
				);
			}
			function resizer() {
				if (!props.resizeable) return null;
				return (
					<div className={style.gridResize} onMouseDown={props.setCurrentResizeTh} data-index={index}>
						&nbsp;
					</div>
				);
			}
		}
	}

	/**
	 * Get the table body for the grid
	 */
	function renderBody() {
		const { columns, values, cellClassName, rowClassName, keyProperty, onClickRow, expandedDisplay, selected } =
			props;

		function onClick(id: any) {
			if (onClickRow) return () => onClickRow(id);
			else return () => {};
		}

		return <tbody>{values.map(renderRow)}</tbody>;

		function renderRow(val: T) {
			const keyVal = val[keyProperty] || JSON.stringify(val);
			const subDisplay = expandedDisplay && expandedDisplay(keyVal);
			if (subDisplay)
				return (
					<>
						{render()}
						<tr
							className={styles(
								style.tableRow,
								rowClassName,
								selected?.includes(keyVal as string | number) && style.selected,
							)}
						>
							<td colSpan={columns.length}>{subDisplay}</td>
						</tr>
					</>
				);
			return render();

			function render() {
				return (
					<tr
						className={styles(
							style.tableRow,
							rowClassName,
							selected?.includes(keyVal as string | number) && style.selected,
						)}
						key={String(keyVal)}
						onClick={onClick(keyVal)}
					>
						{columns.map(renderCell)}
					</tr>
				);
			}

			/**
			 * Get a cell for the table
			 */
			function renderCell(column: Column<T>, index: number) {
				const content = column.getValue(val);
				let strContent = (content || "") as string;
				let overranCharLimit = false;
				if (column.charLimit) {
					strContent = strContent.toString();
					overranCharLimit = column.charLimit && strContent.length > column.charLimit;
					if (overranCharLimit) {
						strContent = strContent.slice(0, column.charLimit) + "[...]";
					}
				}
				const cellContent = column.linkUrl ? (
					<a
						target="_blank"
						rel="noreferrer"
						href={column.linkUrl.replace(`{${column.propertyName}}`, strContent)}
					>
						{strContent}
					</a>
				) : (
					strContent
				);
				return (
					<td
						className={styles(style.tableCell, cellClassName, !column.visible && style.hidden)}
						key={index}
						title={(overranCharLimit && content) || null}
					>
						{cellContent}
					</td>
				);
			}
		}
	}
}

export function ActionsContainer<T>(props: GridMarkupProps<T>) {
	const exportButtonText = props.exportDisabled ? (
		<Spinner tag="span" right>
			{"Exporting..."}
		</Spinner>
	) : (
		"Export"
	);
	return (
		<div className={style.actionsContainer}>
			{props.changeVisibility ? (
				<ToggleColumnsDropdown columns={props.columns} updateVisibility={props.updateColumnVisibility} />
			) : null}
			{props.stateful ? (
				<>
					<Button onClick={props.saveGridConfiguration}>Save Report Settings</Button>
					<Button onClick={props.clearGridConfiguration}>Clear Saved Report Settings</Button>
				</>
			) : null}
			{props.exportable ? (
				<Button onClick={props.excelExportEvent} disabled={props.exportDisabled}>
					{exportButtonText}
				</Button>
			) : null}
		</div>
	);
}
